/**
 * The Desktop below `md`: no floating windows — one full-width canvas and a
 * breadcrumb; opening a folder navigates into it.
 */
import type { ReactNode } from 'react';
import { ArrowLeft, ChevronRight, Monitor } from 'lucide-react';
import type { VaultDocumentFolderDto } from '@/lib/api';
import { ROOT_CONTAINER, TRASH_KEY } from '../../lib/desktop-layout';

/** Below `md`: one full-width canvas and a breadcrumb; opening a folder
 *  navigates into it. The arrangement still applies per folder. */
export function NarrowDesktop({
  container,
  foldersById,
  onNavigate,
  canvas,
  trash,
}: {
  container: string;
  foldersById: ReadonlyMap<string, VaultDocumentFolderDto>;
  onNavigate: (container: string) => void;
  canvas: (container: string) => ReactNode;
  trash: ReactNode;
}) {
  const chain: VaultDocumentFolderDto[] = [];
  if (container !== ROOT_CONTAINER && container !== TRASH_KEY) {
    let cursor = foldersById.get(container);
    for (let guard = 0; cursor && guard < 64; guard += 1) {
      chain.unshift(cursor);
      cursor = cursor.parentFolderId ? foldersById.get(cursor.parentFolderId) : undefined;
    }
  }
  const atRoot = container === ROOT_CONTAINER;
  return (
    <div className="flex min-h-[24rem] flex-1 flex-col">
      <nav
        aria-label="Folder breadcrumbs"
        className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-border bg-muted/30 px-3 py-2 text-sm"
      >
        {!atRoot && (
          <button
            type="button"
            aria-label="Back"
            onClick={() =>
              onNavigate(
                container === TRASH_KEY
                  ? ROOT_CONTAINER
                  : (foldersById.get(container)?.parentFolderId ?? ROOT_CONTAINER),
              )
            }
            className="mr-1 rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onNavigate(ROOT_CONTAINER)}
          disabled={atRoot}
          className="flex shrink-0 items-center gap-1.5 rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:font-medium disabled:text-foreground"
        >
          <Monitor className="h-4 w-4" aria-hidden />
          Desktop
        </button>
        {container === TRASH_KEY && (
          <>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden />
            <span className="shrink-0 px-1.5 py-0.5 font-medium">Trash</span>
          </>
        )}
        {chain.map((folder, i) => (
          <span key={folder.folderId} className="flex shrink-0 items-center gap-1">
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden />
            {i === chain.length - 1 ? (
              <span className="px-1.5 py-0.5 font-medium">{folder.name}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(folder.folderId)}
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {folder.name}
              </button>
            )}
          </span>
        ))}
      </nav>
      <div className="relative min-h-0 flex-1">
        <div className="absolute inset-0">
          {container === TRASH_KEY ? trash : canvas(container)}
        </div>
      </div>
    </div>
  );
}
